import type { ReactNode } from "react";
import type { INavigationNodeDto } from "@/types/filteredContentTypes";
import { NodeLabel } from "./NodeLabel/NodeLabel";
import { getPath, normalizePathForCompare } from "../navigationPathUtils";
import styles from "./NavigationNode.module.scss";

export interface INavigationNodeProps {
  node: INavigationNodeDto;
  currentPath: string;
  searchTerm: string;
  locale: string;
  expandedItems: Set<string>;
  loadedChildren: Map<string, INavigationNodeDto[]>;
  loadingChildren: Set<string>;
  onToggle: (nodeKey: string) => void;
  onLoadChildren: (nodeKey: string) => void;
  level: number;
  /** When true, renders as a simple flat list item (no expand/collapse) */
  isSearchResult?: boolean;
}

// Determine node type based on Umbraco document type
function getNodeType(documentType: string): "folder" | "page" | "other" {
  if (documentType === "genericPageFolder" || documentType === "eligibilityFolder") {
    return "folder";
  }
  if (documentType === "genericPage" || documentType === "engagementDetailsPage" || documentType === "homePage") {
    return "page";
  }
  return "other";
}

export function NavigationNode({
  node,
  currentPath,
  searchTerm,
  locale,
  expandedItems,
  loadedChildren,
  loadingChildren,
  onToggle,
  onLoadChildren,
  level,
  isSearchResult = false,
}: INavigationNodeProps): ReactNode {

  const isExpanded = expandedItems.has(node.key);
  const isLoading = loadingChildren.has(node.key);
  const children = loadedChildren.get(node.key);
  const nodePath = getPath(node.url, locale);
  const isActive = Boolean(
    nodePath && normalizePathForCompare(currentPath) === normalizePathForCompare(nodePath)
  );
  const nodeType = getNodeType(node.documentType);
  const isRootLevel = level === 0;
  const childrenListId = `nav-children-${node.key}`;

  // genericPageFolder should be skipped - render its children directly
  const isGenericPageFolder = node.documentType === "genericPageFolder";

  // Handle expand/collapse click
  const handleToggle = (): void => {
    if (!node.hasChildren) {
      return;
    }
    if (!children && !isLoading) {
      onLoadChildren(node.key);
    }
    onToggle(node.key);
  };

  // Filter children by search term (only for tree view, not search results)
  const filteredChildren =
    searchTerm && children && !isSearchResult
      ? children.filter((c) => c.title.toLowerCase().includes(searchTerm.toLowerCase()))
      : children;

  // Search results are rendered as a simple flat list
  if (isSearchResult) {
    return (
      <div className={styles.searchResultItem} role="treeitem" aria-selected={isActive} data-node-key={node.key} data-testid={`search-result-${node.key}`}>
        <div className={styles.searchResultContainer}>
          <NodeLabel
            node={node}
            nodeType="page"
            nodePath={nodePath}
            isActive={isActive}
            hasChildren={false}
            isRootLevel={false}
            className={styles.searchResultLink}
            onToggle={handleToggle}
          />
        </div>
      </div>
    );
  }

  // For genericPageFolder, check showInPartnerPortal to determine rendering behavior
  // If showInPartnerPortal is true: render as expandable/collapsible folder (accordion)
  // If showInPartnerPortal is false: hide folder, render children directly (current behavior)
  if (isGenericPageFolder && !node.showInPartnerPortal) {
    if (!filteredChildren) {
      // Children are still loading - render nothing (the parent will show loading)
      return null;
    }
    return (
      <>
        {filteredChildren.map((child) => (
          <NavigationNode
            key={child.key}
            node={child}
            currentPath={currentPath}
            searchTerm={searchTerm}
            locale={locale}
            expandedItems={expandedItems}
            loadedChildren={loadedChildren}
            loadingChildren={loadingChildren}
            onToggle={onToggle}
            onLoadChildren={onLoadChildren}
            level={level}
          />
        ))}
      </>
    );
  }

  // CSS classes based on level
  const itemClass = isRootLevel ? styles.treeItem : styles.nestedPageItem;
  const pageContainerClass = isRootLevel ? styles.treeItemHeader : styles.nestedPageContainer;
  const expandBtnClass = isRootLevel ? styles.expandButton : styles.nestedFolderExpandButton;
  const placeholderClass = isRootLevel ? styles.expandPlaceholder : styles.nestedFolderExpandPlaceholder;
  const childListClass = isRootLevel ? styles.nestedFolderList : styles.nestedPageList;

  const getLabelClass = (): string => {
    if (isRootLevel) {
      return styles.categoryName;
    }
    return nodeType === "folder" ? styles.nestedFolderName : styles.nestedPageLink;
  };

  return (
    <div className={itemClass} role="treeitem" aria-selected={isActive} data-node-key={node.key} data-testid={`nav-node-${node.key}`}>
      <div className={pageContainerClass}>
        {node.hasChildren ? (
          <button
            type="button"
            className={expandBtnClass}
            onClick={handleToggle}
            tabIndex={-1}
            aria-hidden="true"
          >
            {isLoading ? (
              <span className={styles.loadingDot} data-testid="loading-indicator">
                •
              </span>
            ) : (
              <span className={styles.chevron} data-expanded={isExpanded}>
                &#xE76C;
              </span>
            )}
          </button>
        ) : (
          <span className={placeholderClass} />
        )}

        <NodeLabel
          node={node}
          nodeType={nodeType}
          nodePath={nodePath}
          isActive={isActive}
          hasChildren={node.hasChildren}
          isRootLevel={isRootLevel}
          isExpanded={node.hasChildren ? isExpanded : undefined}
          controlsId={node.hasChildren ? childrenListId : undefined}
          className={getLabelClass()}
          onToggle={handleToggle}
        />
      </div>

      {isExpanded && filteredChildren && filteredChildren.length > 0 && (
        <div id={childrenListId} className={childListClass} role="group">
          {filteredChildren.map((child) => (
            <NavigationNode
              key={child.key}
              node={child}
              currentPath={currentPath}
              searchTerm={searchTerm}
              locale={locale}
              expandedItems={expandedItems}
              loadedChildren={loadedChildren}
              loadingChildren={loadingChildren}
              onToggle={onToggle}
              onLoadChildren={onLoadChildren}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
